import type { GroupType } from "@/types/entities/approval-groups";
import type { Input } from "@/workflow_api/types/workflow";

/**
 * Serializes the group assignment data into the expected input format.
 * @param groupId - ID of the group to serialize
 * @param assignableLevels - Array of assignable seniority levels (optional)
 * @param flowId - ID of the flow to assign (optional)
 * @returns The serialized group inputs
 */
export const getSerializedGroupInputs = (
  groupId: string,
  assignableLevels?: string[],
  flowId?: string,
): Input => {
  const inputs: Input = [
    {
      name: "group",
      value: groupId,
      type: "STRING",
    },
  ];

  // Add assignable_levels as multiple inputs with the same name
  if (assignableLevels && assignableLevels.length > 0) {
    assignableLevels.forEach((level) => {
      inputs.push({
        name: "assignable_levels",
        value: level,
        type: "STRING",
      });
    });
  }

  // Add flow_id if provided
  if (flowId) {
    inputs.push({
      name: "flow_id",
      value: flowId,
      type: "STRING",
    });
  }

  return inputs;
};

/**
 * Deserializes the group input from the API node.
 * @param nodeInputs - The inputs from the API node
 * @param groups - The list of available groups
 * @returns The deserialized group or null if not found
 */
export const getDeserializedGroupNode = (
  nodeInputs?: Input,
  groups?: GroupType[],
): GroupType | null => {
  if (!nodeInputs || !groups || groups.length === 0) return null;
  const groupInput = nodeInputs.find((input) => input.name === "group");
  if (!groupInput) return null;
  return groups.find((g) => g.id === groupInput.value) || null;
};

/**
 * Deserializes the assignable levels from the API node inputs.
 * @param nodeInputs - The inputs from the API node
 * @returns Array of assignable level values
 */
export const getDeserializedAssignableLevels = (
  nodeInputs?: Input,
): string[] => {
  if (!nodeInputs) return [];
  return nodeInputs
    .filter((input) => input.name === "assignable_levels")
    .map((input) => String(input.value));
};

/**
 * Deserializes the flow ID from the API node inputs.
 * @param nodeInputs - The inputs from the API node
 * @returns The flow ID or null if not found
 */
export const getDeserializedFlowId = (nodeInputs?: Input): string | null => {
  if (!nodeInputs) return null;
  const flowInput = nodeInputs.find((input) => input.name === "flow_id");
  return flowInput ? String(flowInput.value) : null;
};
